import { useEffect } from 'react'
import { useTheme } from '../contexts/ThemeContext'

/**
 * Converts hex color to HSL format for CSS variables
 */
function hexToHsl(hex: string): string {
  // Remove # if present
  hex = hex.replace('#', '')
  
  // Parse RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255
  const g = parseInt(hex.substring(2, 4), 16) / 255
  const b = parseInt(hex.substring(4, 6), 16) / 255
  
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2
  
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
      case g: h = ((b - r) / d + 2) / 6; break
      case b: h = ((r - g) / d + 4) / 6; break
    }
  }
  
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

/**
 * Hook that applies game theme colors as CSS variables
 * Should be called in components that need to apply game-specific theming
 */
export function useGameTheme() {
  const { resolvedTheme, effectiveTheme, gameThemeInput } = useTheme()

  useEffect(() => {
    const root = document.documentElement
    const { core, derived, playerColors } = resolvedTheme
    
    // Set app color shades as CSS variables
    // Map core.accent to app-color-main and app-color-accent
    root.style.setProperty('--app-color-main', core.accent)
    root.style.setProperty('--app-color-background', core.appBackground)
    root.style.setProperty('--app-color-muted', derived.mutedBackground)
    root.style.setProperty('--app-color-accent', core.accent)

    // Set player colors as CSS variables
    playerColors.forEach((playerColor, index) => {
      root.style.setProperty(`--player-${index}-primary`, playerColor.primary)
      root.style.setProperty(`--player-${index}-background`, playerColor.background)
      root.style.setProperty(`--player-${index}-foreground`, playerColor.foreground)
    })
    
    // Clear any player color variables beyond the current player count
    for (let i = playerColors.length; i < 10; i++) {
      root.style.removeProperty(`--player-${i}-primary`)
      root.style.removeProperty(`--player-${i}-background`)
      root.style.removeProperty(`--player-${i}-foreground`)
    }

    // Set UI theme colors as CSS variables - ALWAYS set these to ensure they reset
    // New simplified naming
    root.style.setProperty('--primary', hexToHsl(core.textPrimary)) // text-primary
    root.style.setProperty('--secondary', hexToHsl(derived.textSecondary)) // text-secondary
    root.style.setProperty('--muted', hexToHsl(derived.textMuted)) // text-muted
    root.style.setProperty('--accent', hexToHsl(core.accent)) // text-accent, bg-accent, border-accent
    root.style.setProperty('--danger', hexToHsl(derived.dangerForeground)) // text-danger
    root.style.setProperty('--danger-bg', hexToHsl(core.danger)) // bg-danger
    root.style.setProperty('--inverse', hexToHsl(derived.textInverse)) // text-inverse
    root.style.setProperty('--app', core.appBackground) // bg-app (hex format)
    root.style.setProperty('--surface', hexToHsl(core.surfaceBackground)) // bg-surface
    root.style.setProperty('--secondary-bg', hexToHsl(derived.secondaryBackground)) // bg-secondary
    root.style.setProperty('--muted-bg', hexToHsl(derived.mutedBackground)) // bg-muted
    root.style.setProperty('--basic', hexToHsl(derived.border)) // border-basic
    root.style.setProperty('--focus', hexToHsl(derived.focusRing)) // ring-focus
    
    // Legacy aliases for backward compatibility
    root.style.setProperty('--background', hexToHsl(core.appBackground))
    root.style.setProperty('--foreground', hexToHsl(core.textPrimary))
    root.style.setProperty('--border', hexToHsl(derived.border))
    root.style.setProperty('--input-border', hexToHsl(derived.border))
    root.style.setProperty('--ring', hexToHsl(derived.focusRing))
    root.style.setProperty('--card', hexToHsl(core.surfaceBackground))
    root.style.setProperty('--card-foreground', hexToHsl(core.textPrimary))
    root.style.setProperty('--popover', hexToHsl(core.surfaceBackground))
    root.style.setProperty('--popover-foreground', hexToHsl(core.textPrimary))
    root.style.setProperty('--input', hexToHsl(core.surfaceBackground))
    root.style.setProperty('--input-foreground', hexToHsl(core.textPrimary))
    root.style.setProperty('--destructive', hexToHsl(core.danger))
    root.style.setProperty('--destructive-foreground', hexToHsl(derived.dangerForeground))
    
    // Set subtle background colors
    root.style.setProperty('--subtle', hexToHsl(derived.subtleBackground))
    root.style.setProperty('--subtle-hover', hexToHsl(derived.mutedBackground))
    
    // Set overlay color (for dialog overlays)
    root.style.setProperty('--overlay', derived.overlayBackground)
  }, [resolvedTheme, effectiveTheme, gameThemeInput])
}
